import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { useOrgTenants, useTenants } from "../hooks/resources.ts";
import type { Tenant } from "../api/client.ts";
import { useOrganization } from "./OrganizationContext.tsx";

const STORAGE_KEY = "vayken_selected_tenant";
// Read until everyone has stored a pick under the new key.
const LEGACY_STORAGE_KEY = "scaleupxq_selected_tenant";

interface TenantContextValue {
  selectedTenantId: string | null;
  setSelectedTenantId: (id: string | null) => void;
  /** The selected tenant, or null when none is selected or it isn't loaded yet. */
  selectedTenant: Tenant | null;
  tenants: Tenant[];
  isLoading: boolean;
  /** Slug of the currently-selected tenant, or null when none is selected. */
  tenantSlug: string | null;
  /** Build a tenant-scoped URL, e.g. tenantPath("/companies") -> "/t/<slug>/companies". */
  tenantPath: (subpath?: string) => string;
}

const TenantContext = createContext<TenantContextValue | null>(null);

/**
 * Build a URL under a given tenant, e.g. pathForTenant("acme", "/companies/1")
 * -> "/t/acme/companies/1". Without a slug the subpath is returned unprefixed.
 */
export function pathForTenant(slug: string | null | undefined, subpath = ""): string {
  const clean = subpath ? (subpath.startsWith("/") ? subpath : `/${subpath}`) : "";
  return slug ? `/t/${slug}${clean}` : clean || "/";
}

/** Tenant-relative path of the Collections page (prospect lists). */
export const COLLECTIONS_PATH = "/prospects/collections";

/**
 * Tenant-relative path of the Collections page, or of one collection (prospect
 * list). Pass it to `tenantPath(...)` / `pathForTenant(slug, ...)`.
 */
export function collectionPath(id?: string): string {
  return id ? `${COLLECTIONS_PATH}/${id}` : COLLECTIONS_PATH;
}

/** Extract the tenant slug from a `/t/:tenantSlug/...` pathname, if present. */
function matchTenantSlug(pathname: string): string | null {
  const m =
    matchPath("/t/:tenantSlug/*", pathname) ?? matchPath("/t/:tenantSlug", pathname);
  return m?.params.tenantSlug ?? null;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { selectedOrgId } = useOrganization();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const urlSlug = matchTenantSlug(pathname);

  // Tenant remembered across unprefixed pages / between sessions.
  const [storedTenantId, setStoredTenantIdState] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_STORAGE_KEY),
  );

  // Fetch all accessible tenants (full Tenant objects with slug, logo_url etc.)
  const { data: allTenants = [], isLoading: isLoadingTenants } = useTenants();

  // Fetch org-specific tenant assignments
  const { data: orgTenants, isLoading: isLoadingOrgTenants } = useOrgTenants(selectedOrgId);

  // Filter tenants to only those belonging to the selected org
  const tenants = useMemo(() => {
    if (!selectedOrgId || !orgTenants) return allTenants;
    const orgTenantIds = new Set(orgTenants.map((ot) => ot.tenant_id));
    return allTenants.filter((t) => orgTenantIds.has(t.id));
  }, [selectedOrgId, orgTenants, allTenants]);

  const isLoading = isLoadingTenants || (!!selectedOrgId && isLoadingOrgTenants);

  // Tenant the URL points at (null when off a tenant-scoped route or slug unknown).
  const urlTenant = useMemo(
    () => (urlSlug ? tenants.find((t) => t.slug === urlSlug) ?? null : null),
    [urlSlug, tenants],
  );

  // The URL is the source of truth when on a /t/:slug route; otherwise fall back
  // to the remembered tenant.
  const selectedTenantId = urlSlug ? urlTenant?.id ?? null : storedTenantId;

  const selectedTenant = useMemo(
    () => tenants.find((t) => t.id === selectedTenantId) ?? null,
    [tenants, selectedTenantId],
  );

  const persist = useCallback((id: string | null) => {
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    setStoredTenantIdState(id);
  }, []);

  // Mirror a URL-driven selection into localStorage so unprefixed pages and
  // default redirects reuse the same tenant.
  useEffect(() => {
    if (urlTenant && urlTenant.id !== storedTenantId) persist(urlTenant.id);
  }, [urlTenant, storedTenantId, persist]);

  const setSelectedTenantId = useCallback(
    (id: string | null) => {
      persist(id);
      // When on a tenant-scoped URL, navigate to the same sub-path under the new
      // tenant so the URL stays in sync with the selection.
      const tenant = id ? tenants.find((t) => t.id === id) ?? null : null;
      if (urlSlug && tenant && tenant.slug !== urlSlug) {
        const rest = pathname.replace(`/t/${urlSlug}`, "");
        navigate(`/t/${tenant.slug}${rest}`);
      }
    },
    [persist, tenants, urlSlug, pathname, navigate],
  );

  // Auto-select a default tenant for the unprefixed (remembered) flow.
  useEffect(() => {
    if (isLoading || urlSlug) return; // URL flow drives its own selection
    if (tenants.length === 0) {
      if (storedTenantId) persist(null);
      return;
    }
    if (!storedTenantId || !tenants.some((t) => t.id === storedTenantId)) {
      persist(tenants[0].id);
    }
  }, [storedTenantId, tenants, isLoading, urlSlug, persist]);

  // If the URL names an unknown/inaccessible tenant, bounce to the default one.
  useEffect(() => {
    if (isLoading || !urlSlug || urlTenant || tenants.length === 0) return;
    const fallback = tenants.find((t) => t.id === storedTenantId) ?? tenants[0];
    const rest = pathname.replace(`/t/${urlSlug}`, "");
    navigate(`/t/${fallback.slug}${rest}`, { replace: true });
  }, [isLoading, urlSlug, urlTenant, tenants, storedTenantId, pathname, navigate]);

  const tenantSlug = selectedTenant?.slug ?? null;

  const tenantPath = useCallback(
    (subpath = "") => pathForTenant(tenantSlug, subpath),
    [tenantSlug],
  );

  return (
    <TenantContext.Provider
      value={{
        selectedTenantId,
        setSelectedTenantId,
        selectedTenant,
        tenants,
        isLoading,
        tenantSlug,
        tenantPath,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used within TenantProvider");
  return ctx;
}
